import { useEffect, useState } from 'react';
import { BottomSheet } from '../components/BottomSheet';
import { CopyRoutePicker } from '../components/CopyRoutePicker';
import { BottomNav, SyncBadge } from '../components/Layout';
import { StoreGeoNote } from '../components/StoreGeoNote';
import { StorePicker } from '../components/StorePicker';
import { cardCls, primaryBtn } from '../components/ui/classes';
import { PageHeader } from '../components/ui/PageHeader';
import { SheetAction } from '../components/ui/SheetAction';
import { SkeletonRows } from '../components/ui/Skeleton';
import { StoreForm } from '../components/ui/StoreForm';
import { Toast, toast, type ToastMessage } from '../components/ui/Toast';
import { useStoreDetails } from '../components/ui/useStoreDetails';
import { backfillStoreGeo, createStore, deleteStore, mergeStoreInto, resetStoreOrder, updateStore, useStores, type StoreView } from '../data/stores';

type Editing = StoreView | 'new';

export function StoresPage() {
  const stores = useStores();
  const details = useStoreDetails(stores);
  // Edytowany sklep zostaje w stanie po zamknięciu, żeby arkusz miał treść podczas animacji wyjścia.
  const [editing, setEditing] = useState<Editing | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Sklep łączony z innym (duplikat) — wybór drugiego sklepu w StorePicker.
  const [mergeFrom, setMergeFrom] = useState<StoreView | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Sklep, do którego kopiujemy trasę innego sklepu.
  const [copyTo, setCopyTo] = useState<StoreView | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);

  // Sklepy z adresem bez współrzędnych dostają położenie w tle (najwyżej 5 naraz).
  useEffect(() => void backfillStoreGeo().catch(() => {}), []);

  const open = (e: Editing) => {
    setEditing(e);
    setSheetOpen(true);
  };
  const close = () => setSheetOpen(false);

  const startMerge = (s: StoreView) => {
    setSheetOpen(false);
    setMergeFrom(s);
    setPickerOpen(true);
  };
  const merge = async (survivorId: string) => {
    if (!mergeFrom) return;
    const survivor = stores?.find((s) => s.id === survivorId);
    const into = survivor ? describe(survivor, details(survivor)) : '„Bez nazwy”';
    const from = describe(mergeFrom, details(mergeFrom));
    if (!confirm(`To ten sam sklep? Trasy obu sklepów połączą się w jedną.\n\nZostanie: ${into}\nZniknie: ${from}`)) return;
    try {
      await mergeStoreInto(mergeFrom.id, survivorId);
      setMessage(toast(`Połączono z „${survivor?.name || 'Bez nazwy'}”.`));
    } catch {
      setMessage(toast('Nie udało się połączyć sklepów.'));
    }
  };

  const startCopy = (s: StoreView) => {
    setSheetOpen(false);
    setCopyTo(s);
    setCopyOpen(true);
  };

  return (
    <>
      <PageHeader title="Sklepy" right={<SyncBadge />} />
      <main className="flex flex-1 flex-col gap-4 p-4">
        {stores === undefined ? (
          <SkeletonRows />
        ) : stores.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-2 text-center">
            <h2 className="text-xl font-semibold">Brak sklepów</h2>
            <p className="text-stone-600 dark:text-stone-400">
              Dodaj sklep, w którym robisz zakupy. Odhaczka nauczy się w nim kolejności alejek.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {stores.map((s) => (
              <StoreItem key={s.id} store={s} details={details(s)} onEdit={() => open(s)} />
            ))}
          </ul>
        )}
        <div className="mt-auto pt-2">
          <button type="button" onClick={() => open('new')} className={primaryBtn}>
            + Dodaj sklep
          </button>
        </div>
      </main>
      <BottomNav />

      <BottomSheet open={sheetOpen} onClose={close} title={editing === 'new' ? 'Nowy sklep' : 'Sklep'}>
        {editing && <StoreSheet editing={editing} onDone={close} onMerge={startMerge} onCopy={startCopy} canMerge={(stores?.length ?? 0) > 1} />}
      </BottomSheet>
      <StorePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(id) => void merge(id)}
        title={`Połącz „${mergeFrom?.name || 'Bez nazwy'}” z…`}
        excludeId={mergeFrom?.id ?? null}
        allowAdd={false}
      />
      <CopyRoutePicker target={copyTo} open={copyOpen} onClose={() => setCopyOpen(false)} onDone={(text) => setMessage(toast(text))} />
      <Toast message={message} onDismiss={() => setMessage(null)} />
    </>
  );
}

/** „Auchan Test” (ul. Kasprowicza 145 · ostatnio 11.10) — do potwierdzeń, gdy nazwy się powtarzają. */
const describe = (s: StoreView, details: string) => `„${s.name || 'Bez nazwy'}”${details ? ` (${details})` : ''}`;

function StoreItem({ store, details, onEdit }: { store: StoreView; details: string; onEdit: () => void }) {
  return (
    <li className={`${cardCls} flex flex-wrap items-stretch overflow-hidden`}>
      <button type="button" onClick={onEdit} className="min-w-0 flex-1 px-4 py-3 text-left active:bg-stone-50 dark:active:bg-stone-800">
        <span className="block truncate font-semibold">{store.name || 'Bez nazwy'}</span>
        {details && <span className="block truncate text-sm text-stone-500">{details}</span>}
      </button>
      <button
        type="button"
        aria-label={store.favorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
        aria-pressed={!!store.favorite}
        onClick={() => void updateStore(store.id, { favorite: !store.favorite })}
        className={`flex w-14 shrink-0 items-center justify-center text-2xl active:bg-stone-50 dark:active:bg-stone-800 ${
          store.favorite ? 'text-amber-500' : 'text-stone-300 dark:text-stone-600'
        }`}
      >
        {store.favorite ? '★' : '☆'}
      </button>
      <StoreGeoNote storeId={store.id} variant="list" />
    </li>
  );
}

interface SheetProps {
  editing: Editing;
  onDone: () => void;
  onMerge: (store: StoreView) => void;
  onCopy: (store: StoreView) => void;
  /** Są inne sklepy (do połączenia albo skopiowania trasy). */
  canMerge: boolean;
}

function StoreSheet({ editing, onDone, onMerge, onCopy, canMerge }: SheetProps) {
  if (editing === 'new')
    return (
      <StoreForm
        withAddress
        autoFocus
        submitLabel="Dodaj sklep"
        onSubmit={async (d) => {
          await createStore(d);
          onDone();
        }}
      />
    );

  const store = editing;
  const name = store.name || 'Bez nazwy';

  const reset = async () => {
    if (!confirm(`Zapomnieć kolejność w „${name}”?\n\nAplikacja nauczy się kolejności od nowa przy następnych zakupach.`)) return;
    await resetStoreOrder(store.id);
    onDone();
  };
  const remove = async () => {
    if (!confirm(`Usunąć sklep „${name}”?\n\nAktywne listy z tym sklepem zostaną bez sklepu.`)) return;
    await deleteStore(store.id);
    onDone();
  };

  return (
    <div className="flex flex-col gap-4">
      <StoreGeoNote storeId={store.id} variant="sheet" />
      <StoreForm
        withAddress
        initial={{ name: store.name ?? '', chainName: store.chainName ?? '', address: store.address ?? '' }}
        submitLabel="Zapisz"
        onSubmit={async (d) => {
          await updateStore(store.id, d);
          onDone();
        }}
      />
      <div className="-mx-1 flex flex-col border-t border-stone-200 pt-2 dark:border-stone-800">
        {canMerge && (
          <SheetAction onClick={() => onMerge(store)} hint="Gdy ten sam sklep jest na liście dwa razy">
            Połącz z innym sklepem…
          </SheetAction>
        )}
        {canMerge && (
          <SheetAction onClick={() => onCopy(store)} hint="Np. ze sklepu tej samej sieci o podobnym układzie">
            Skopiuj trasę z innego sklepu…
          </SheetAction>
        )}
        <SheetAction onClick={reset} hint="Gdy sklep przestawił alejki">
          Zapomnij kolejność w tym sklepie
        </SheetAction>
        <SheetAction tone="danger" onClick={remove}>
          Usuń sklep
        </SheetAction>
      </div>
    </div>
  );
}
